import { useCallback } from 'react'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import { Flex } from 'antd'
import { ListError, PageHeader } from '../../Common'
import { useIsAdmin, useFeatureGuard } from '../../../redux/slices/session'
import api from '../../../api'
import { useAsync } from '../../../hooks'
import useLocalStorage from '../../../hooks/useLocalStorage'
import { PAGE_SIZE } from '../../../lib/constants'
import MonthFilter from '../../Common/MonthFilter'
import Filters from '../../Forms/Filters'
import useFilters from './useFilters'
import BillingOrders from './BillingOrders'

const DEFAULT_FILTERS = {
  checkDate: dayjs().startOf('month').format('YYYY-MM'),
  query: '',
  page: 1,
  showInactive: false,
}


export default function OfficeBillingOrders() {
  const { practiceId } = useParams()
  const isAdmin = useIsAdmin()
  const getPractice = useCallback(
    () => api.practices.getPractice(practiceId),
    [practiceId],
  )
  const { pending, value: practice, error, clearError } = useAsync(getPractice)

  const { handleFilterChange, conditions, setConditions } =
    useFilters(DEFAULT_FILTERS)

  const { page, checkDate, query, perPage: urlPerPage, showInactive } = conditions
  const [localStoragePageSize, setPageSize] = useLocalStorage('pageSize', PAGE_SIZE)
  const perPage = urlPerPage || localStoragePageSize

  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      if (newPageSize !== Number(perPage)) {
        setPageSize(newPageSize)
        setConditions({ ...conditions, page: newPage, perPage: newPageSize })
      } else {
        setConditions({ ...conditions, page: newPage })
      }
    },
    [perPage, conditions, setConditions, setPageSize],
  )

  useFeatureGuard('in_office_checks')

  if (checkDate && !pending) {
    return (
      <div>
        <PageHeader
          title="Office Billing Orders"
          subTitle={isAdmin && practice.name}
          hideSubTitleOnMobile
        />
        <Flex gap={10}>
          <MonthFilter
            onSubmit={({ month }) => handleFilterChange({ checkDate: month })}
            currentValue={checkDate}
          />

          <Filters
            defaultValues={DEFAULT_FILTERS}
            currentValues={{ query, showInactive }}
            onSubmit={({ query: newQuery, showInactive: newShowInactive }) =>
              handleFilterChange({
                query: newQuery,
                showInactive: newShowInactive || undefined,
              })
            }
            placeholders={{ query: 'Search by Name, DOB, or MRN' }}
            showSearch
            showInactiveCheck
          />
        </Flex>

        <BillingOrders
          checkDate={checkDate}
          onPaginationChange={handlePaginationChange}
          page={page}
          perPage={perPage}
          practiceId={practiceId}
          query={query}
          showInactive={showInactive}
        />
      </div>
    )
  }

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
    </div>
  )
}
